import { useState, FormEvent } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../store/authStore'
import { track } from '../lib/tracker'
import GitHubAuthButton from '../components/GitHubAuthButton'
import { isSelfHosted, branding } from '../config/edition'
import { Alert, Button, Card, Field, Input } from '../components/ui'

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const login = useAuthStore((s) => s.login)
  const navigate = useNavigate()
  const { t } = useTranslation()

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (loading) return
    setError('')
    setLoading(true)
    try {
      await login(email, password)
      track('login')
      navigate('/dashboard')
    } catch {
      setError(t('login.error'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">{branding.appName}</h1>
          <p className="text-gray-500 text-sm mt-1">{t('login.subtitle')}</p>
        </div>

        <Card>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <Alert tone="danger">{error}</Alert>}

            <Field label={t('login.email')}>
              {(p) => (
                <Input
                  {...p}
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  placeholder={t('login.emailPlaceholder')}
                />
              )}
            </Field>

            {/* The "forgot password?" link sits on the label row. It is placed
                over the row rather than inside <label>, because an anchor
                inside a label steals the click that should focus the input. */}
            <div className="relative">
              <Field label={t('login.password')}>
                {(p) => (
                  <Input
                    {...p}
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                  />
                )}
              </Field>
              <Link
                to="/forgot-password"
                className="absolute right-0 top-0.5 text-xs text-brand-600 hover:text-brand-700"
              >
                {t('login.forgotPassword')}
              </Link>
            </div>

            <Button type="submit" loading={loading} fullWidth>
              {loading ? t('login.submitting') : t('login.submit')}
            </Button>

            {/* GitHub sign-in + self-signup are SaaS-only. */}
            {!isSelfHosted && (
              <>
                <div className="flex items-center gap-2 my-1">
                  <div className="h-px flex-1 bg-gray-200" />
                  <span className="text-xs text-gray-400 uppercase tracking-wide">{t('login.or')}</span>
                  <div className="h-px flex-1 bg-gray-200" />
                </div>

                <GitHubAuthButton mode="signin" />
              </>
            )}
          </form>
        </Card>

        {!isSelfHosted && (
          <p className="text-center text-sm text-gray-500 mt-4">
            {t('login.noAccount')}{' '}
            <Link to="/register" className="text-brand-600 hover:text-brand-700 font-medium">
              {t('login.signUp')}
            </Link>
          </p>
        )}
      </div>
    </div>
  )
}
